import React, { memo } from 'react';
import { SidebarBoundary } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/ui/sidebar-boundary/index.tsx';
import { SkeletonNavMenuItems } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/ui/skeleton-nav-menu-items/main.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import { fg } from '@atlassian/jira-feature-gating';
import { componentWithFG } from '@atlassian/jira-feature-gate-component';
import type { Nav4RoadmapsProps } from './ui/roadmaps/index.tsx';
import { PACKAGE_NAME, TEAM_NAME } from './common/constants.tsx';
import { RoadmapsStatic } from './ui/roadmaps/static/index.tsx';
import { ActionButtonsStatic } from './common/ui/action-buttons-static/index.tsx';
import { useIsCollectionSelected } from './common/utils/router.tsx';
import { useLocalIsExpanded } from './common/utils/use-local-is-expanded.tsx';

/**
 *
 * The continued usage of RLL goes against the guidance given to teams that implemented Nav4:
 * "Do not use RLL. Use entrypoints instead."
 *
 * See https://go/entrypoints-migration-runbook
 *
 * Due to the recent SSR & performance changes made to this module, more consulting is needed with
 * the Magma before it can be fully removed.
 */
const LazySidebarContent = lazyForPaint(
	() =>
		import(
			/* webpackChunkName: "async-jira-navigation-apps-sidebar-nav4-sidebar-roadmaps" */ './ui/roadmaps/index.tsx'
		).then(({ Nav4RoadmapsContainer }) => Nav4RoadmapsContainer),
	{
		includeInShellOnlySsrBundle: true,
		entryFsSsrLiftImportToModule: true,
	},
);

const Nav4RoadmapsOld = memo((props: Nav4RoadmapsProps) => {
	const { isExpanded, ...dndProps } = props;
	const isCollectionSelected = useIsCollectionSelected();

	const isExpandedValue = fg('navx-global-side-nav')
		? !!dndProps.isExpandOrFlyoutOpen && isExpanded
		: isExpanded;

	return (
		<SidebarBoundary
			id="nav4.sidebar.roadmaps"
			packageName={PACKAGE_NAME}
			teamName={TEAM_NAME}
			ufoName="nav4-sidebar-roadmaps"
		>
			<Placeholder
				name="nav4-sidebar-roadmaps"
				fallback={
					<RoadmapsStatic
						actionsOnHover={<ActionButtonsStatic />}
						isExpanded={isExpandedValue}
						{...dndProps}
					>
						{isCollectionSelected && <SkeletonNavMenuItems />}
					</RoadmapsStatic>
				}
			>
				<LazySidebarContent isExpanded={isExpandedValue} {...dndProps} />
			</Placeholder>
		</SidebarBoundary>
	);
});

const Nav4RoadmapsNew = memo((props: Nav4RoadmapsProps) => {
	const { isExpanded, ...dndProps } = props;
	const isCollectionSelected = useIsCollectionSelected();

	const isExpandedValue = fg('navx-global-side-nav')
		? !!dndProps.isExpandOrFlyoutOpen && isExpanded
		: isExpanded;
	const [localIsExpanded, setLocalIsExpanded] = useLocalIsExpanded(isExpandedValue);

	return (
		<SidebarBoundary
			id="nav4.sidebar.roadmaps"
			packageName={PACKAGE_NAME}
			teamName={TEAM_NAME}
			ufoName="nav4-sidebar-roadmaps"
		>
			<RoadmapsStatic
				actionsOnHover={<ActionButtonsStatic />}
				isExpanded={localIsExpanded}
				onExpansionToggle={setLocalIsExpanded}
				{...dndProps}
			>
				<Placeholder
					name="nav4-sidebar-roadmaps"
					fallback={isCollectionSelected && <SkeletonNavMenuItems />}
				>
					<LazySidebarContent isExpanded={localIsExpanded} {...dndProps} />
				</Placeholder>
			</RoadmapsStatic>
		</SidebarBoundary>
	);
});

export const Nav4Roadmaps: React.FC<Nav4RoadmapsProps> = componentWithFG(
	'navx-2879-fix-roadmaps-dnd',
	Nav4RoadmapsNew,
	Nav4RoadmapsOld,
);
